import { createContext, ReactNode, useContext, useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import { useSignupFieldTracking } from '../../../hooks/useSignupFieldTracking';
import {
  getAmplitudeDefaultEventProperties,
  getAmplitudeDeviceCategory,
  SIGNUP_FLOW_DE_MOBILE,
} from '../../../utils/amplitudeEvents';
import { useMobileSignupNavContext } from '../hooks/useMobileSignupNavContext';
import { TMobileSignupStage } from '../screens/types';

type TStageFieldTracking = ReturnType<typeof useSignupFieldTracking>;

const StageFieldTrackingContext = createContext<TStageFieldTracking | null>(
  null,
);

// One tracking instance, mounted here (above AnimatePresence) and keyed by
// stage, so the hook's field_index / fields_completed refs accumulate across a
// stage's screens and reset only when the stage changes — matching the desktop
// baseline where a stage's fields share one page.
const StageInstance = ({
  stage,
  children,
}: {
  stage: TMobileSignupStage;
  children: ReactNode;
}) => {
  const stageTracking = useSignupFieldTracking(stage);

  // Fires once on mount. StageInstance is keyed by stage, so a stage change
  // remounts it and re-fires — no dep needed.
  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': stage,
      client_device: getAmplitudeDeviceCategory(),
      signup_flow: SIGNUP_FLOW_DE_MOBILE,
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  return (
    <StageFieldTrackingContext.Provider value={stageTracking}>
      {children}
    </StageFieldTrackingContext.Provider>
  );
};

export const MobileSignupFieldTrackingProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { stage } = useMobileSignupNavContext();

  return (
    <StageInstance key={stage} stage={stage}>
      {children}
    </StageInstance>
  );
};

export const useStageFieldTracking = (): TStageFieldTracking => {
  const context = useContext(StageFieldTrackingContext);
  if (!context) {
    throw new Error(
      'useStageFieldTracking must be used within a MobileSignupFieldTrackingProvider',
    );
  }
  return context;
};
